
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";

export const SendSupervisionRequest = () => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [successAlert, setSuccessAlert] = useState(false);
  const [sentToEmail, setSentToEmail] = useState("");
  const { toast } = useToast();
  const { t } = useTranslation('supervision');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccessAlert(false);

    try {
      // Récupérer l'ID utilisateur du coach connecté
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error(t('sendRequest.errors.notAuthenticated'));

      // Récupérer le profil coach
      const { data: coachData, error: coachError } = await supabase
        .from("coaches")
        .select("id")
        .eq("user_id", user.id)
        .single();

      if (coachError) throw new Error(t('sendRequest.errors.mustBeCoach'));

      // Chercher l'utilisateur par email de manière sécurisée
      // Utiliser auth.users qui est accessible via RPC sécurisé pour les recherches par email
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("id")  // Ne plus récupérer l'email directement
        .eq("email", email)
        .maybeSingle();

      if (profileError) {
        throw new Error(t('sendRequest.errors.userSearchError'));
      }

      if (!profileData) {
        throw new Error(t('sendRequest.errors.userNotFound', { email }));
      }

      // Vérifier s'il existe déjà une demande active (pending ou accepted seulement)
      const { data: existingRequest, error: existingError } = await supabase
        .from("supervision_requests")
        .select("id, status")
        .eq("coach_id", coachData.id)
        .eq("trainee_user_id", profileData.id)
        .in("status", ["pending", "accepted"])
        .maybeSingle();

      if (existingError) {
        throw new Error(t('sendRequest.errors.checkExistingError'));
      }

      if (existingRequest) {
        if (existingRequest.status === "pending") {
          throw new Error(t('sendRequest.errors.pendingRequest'));
        } else if (existingRequest.status === "accepted") {
          throw new Error(t('sendRequest.errors.acceptedRequest'));
        }
      }

      // Créer la demande de supervision
      const { error: requestError } = await supabase
        .from("supervision_requests")
        .insert({
          coach_id: coachData.id,
          trainee_user_id: profileData.id,
          message: message || null
        });

      if (requestError) {
        throw requestError;
      }

      // Afficher l'alerte de succès
      setSuccessAlert(true);
      setSentToEmail(email);

      toast({
        title: t('sendRequest.success.title'),
        description: t('sendRequest.success.description'),
      });

      setEmail("");
      setMessage("");
    } catch (error: any) {
      console.error("Erreur lors de l'envoi de la demande:", error);
      toast({
        variant: "destructive",
        title: t('sendRequest.errors.title'),
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto space-y-4">
      {successAlert && (
        <Alert className="border-green-200 bg-green-50 text-green-800">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="font-medium">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              {t('sendRequest.success.sentTo')} {sentToEmail}
            </div>
            <p className="text-sm mt-1 text-green-700">
              {t('sendRequest.success.notification')}
            </p>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('sendRequest.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">{t('sendRequest.emailLabel')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder={t('sendRequest.emailPlaceholder')}
                className="focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <Label htmlFor="message">{t('sendRequest.messageLabel')}</Label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('sendRequest.messagePlaceholder')}
                rows={4}
                className="focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500" 
              disabled={loading}
            >
              {loading ? t('sendRequest.sendingButton') : t('sendRequest.sendButton')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
